/**
 * SequencePage — sequence editor (§11 D–G, T-11).
 *
 * Two inner tabs:
 *   - Editor (default): vertical list of `SequenceBlockCard`s separated by
 *     `SequenceWaitConnector`s, plus 340 px sticky right rail with delivery settings.
 *   - Recipients: dense Airtable-style list of enrolled candidates, wired to
 *     `sequenceRecipientsService` (C-21). Consumes the Smart Match enrolment
 *     stash on mount, so clicking "Create sequence" from a Smart Match result
 *     enrols the ticked candidates into this sequence automatically.
 *
 * Persistence:
 *   - Sequence rows + steps → `emailSequenceService` (existing Supabase service).
 *   - Per-sequence delivery settings → localStorage key `ggj.sequences.v1` (keyed
 *     by sequence id) because the current schema doesn't persist these yet.
 *     TODO(§8-v2): sync settings to a new column / table.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Save,
  AlertCircle,
  MoreHorizontal,
  UserMinus,
  RotateCw,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import {
  emailSequenceService,
  type EmailSequence,
  type EmailSequenceStep,
} from '@core/supabase/services/emailSequenceService';
import {
  sequenceRecipientsService,
  type SequenceRecipient,
  type SequenceRecipientStatus,
} from '@core/supabase/services/sequenceRecipientsService';
import { queryKeys } from '@core/query/queryKeys';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { useGlobalToast } from '@shared/components/Toast/ToastProvider';
import {
  consumePendingRecipients,
  peekPendingRecipients,
} from '@modules/smart-match/lib/pendingSequenceRecipients';
import {
  SequenceBlockCard,
  type SequenceBlockData,
} from './components/SequenceBlockCard';
import { SequenceWaitConnector } from './components/SequenceWaitConnector';
import {
  SequenceSettingsRail,
  DEFAULT_SEQUENCE_SETTINGS,
  type SequenceSettings,
} from './components/SequenceSettingsRail';

// ── localStorage helpers (§11-F settings + §11-E wait-days) ────────────────────

const LS_KEY = 'ggj.sequences.v1';

interface LocalSequenceState {
  settings: SequenceSettings;
  /** Map of stepId → delay_days override (persisted locally until schema migration). */
  waits: Record<string, number>;
}

function readLocalState(id: string): LocalSequenceState {
  try {
    const all = JSON.parse(localStorage.getItem(LS_KEY) || '{}') as Record<string, LocalSequenceState>;
    const existing = all[id];
    if (existing) {
      return {
        settings: { ...DEFAULT_SEQUENCE_SETTINGS, ...existing.settings },
        waits: existing.waits ?? {},
      };
    }
  } catch {
    /* ignore */
  }
  return { settings: { ...DEFAULT_SEQUENCE_SETTINGS }, waits: {} };
}

function writeLocalState(id: string, state: LocalSequenceState) {
  try {
    const all = JSON.parse(localStorage.getItem(LS_KEY) || '{}') as Record<string, LocalSequenceState>;
    all[id] = state;
    localStorage.setItem(LS_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

// ── Page ──────────────────────────────────────────────────────────────────────

type InnerTab = 'editor' | 'recipients';

export function SequencePage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab');
  const tab: InnerTab = rawTab === 'recipients' ? 'recipients' : 'editor';
  const setTab = (t: InnerTab) => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (t === 'editor') n.delete('tab');
        else n.set('tab', t);
        return n;
      },
      { replace: true },
    );
  };

  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sequence, setSequence] = useState<EmailSequence | null>(null);
  const [steps, setSteps] = useState<EmailSequenceStep[]>([]);
  const [local, setLocal] = useState<LocalSequenceState>(() => readLocalState(id));
  const [saving, setSaving] = useState<null | 'step' | 'toggle' | 'bulk'>(null);
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());

  // ── C-21: one-shot stash consumption for Smart Match → Create sequence ──
  // When the user clicked "Create sequence" in NextStepBridge with candidates
  // ticked, the ids were stashed in sessionStorage. Once a real sequence row
  // loads here, consume the stash (matched by threadId when present) and
  // enrol. Guarded so a single sequence id consumes at most once per mount.
  const stashConsumedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sequence) return;
    if (stashConsumedForRef.current === sequence.id) return;

    const pending = peekPendingRecipients();
    if (!pending) return;

    const urlThread = searchParams.get('threadId');
    if (urlThread && pending.threadId !== urlThread) return;

    const consumed = consumePendingRecipients();
    if (!consumed || consumed.candidateIds.length === 0) return;
    stashConsumedForRef.current = sequence.id;

    void sequenceRecipientsService
      .enroll(sequence.id, consumed.candidateIds)
      .then(() => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.sequences.recipients(sequence.id),
        });
        showToast({
          variant: 'success',
          title: 'Recipients enrolled',
          body: `Added ${consumed.candidateIds.length} candidate${consumed.candidateIds.length === 1 ? '' : 's'} to this sequence.`,
        });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        showToast({
          variant: 'error',
          title: 'Could not enrol recipients',
          body: msg,
        });
      });
  }, [sequence, searchParams, queryClient, showToast]);

  // ── Initial load ──
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    emailSequenceService
      .get(id)
      .then((data) => {
        setSequence(data);
        setSteps(data.steps.sort((a, b) => a.step_order - b.step_order));
        setLocal(readLocalState(id));
      })
      .catch((err) => setError(err?.message ?? 'Failed to load sequence'))
      .finally(() => setLoading(false));
  }, [id]);

  // ── Local-state persistence ──
  useEffect(() => {
    if (!id) return;
    writeLocalState(id, local);
  }, [id, local]);

  const updateSettings = useCallback((patch: Partial<SequenceSettings>) => {
    setLocal((prev) => ({ ...prev, settings: { ...prev.settings, ...patch } }));
  }, []);

  // ── Step editing ──
  const patchStep = (stepId: string, patch: Partial<Pick<EmailSequenceStep, 'subject' | 'body_html'>>) => {
    setSteps((prev) => prev.map((s) => (s.id === stepId ? { ...s, ...patch } : s)));
    setDirtyIds((prev) => new Set(prev).add(stepId));
  };

  const saveAllDirty = async () => {
    if (dirtyIds.size === 0) return;
    setSaving('bulk');
    try {
      const targets = steps.filter((s) => dirtyIds.has(s.id));
      await Promise.all(
        targets.map((t) =>
          emailSequenceService.updateStep(t.id, {
            step_order: t.step_order,
            delay_days: local.waits[t.id] ?? t.delay_days,
            template_key: t.template_key,
            subject: t.subject,
            body_html: t.body_html,
          }),
        ),
      );
      setDirtyIds(new Set());
    } finally {
      setSaving(null);
    }
  };

  const addBlock = async () => {
    setSaving('step');
    try {
      const nextOrder = (steps[steps.length - 1]?.step_order ?? 0) + 1;
      const created = await emailSequenceService.addStep(id, {
        step_order: nextOrder,
        delay_days: steps.length === 0 ? 0 : 3,
        template_key: '',
        subject: '',
        body_html: '',
      });
      setSteps((prev) => [...prev, created].sort((a, b) => a.step_order - b.step_order));
    } finally {
      setSaving(null);
    }
  };

  const deleteBlock = async (stepId: string) => {
    if (!confirm('Delete this block? Recipients currently on this step will skip ahead.')) return;
    await emailSequenceService.deleteStep(stepId);
    setSteps((prev) => prev.filter((s) => s.id !== stepId));
    setDirtyIds((prev) => {
      const n = new Set(prev);
      n.delete(stepId);
      return n;
    });
    setLocal((prev) => {
      const waits = { ...prev.waits };
      delete waits[stepId];
      return { ...prev, waits };
    });
  };

  const updateWait = (stepId: string, days: number) => {
    setLocal((prev) => ({ ...prev, waits: { ...prev.waits, [stepId]: days } }));
    // Persist to server as part of the step row (field exists).
    // TODO(§8-v2): expose a dedicated updateStep to avoid this patch path.
    setSteps((prev) => prev.map((s) => (s.id === stepId ? { ...s, delay_days: days } : s)));
    setDirtyIds((prev) => new Set(prev).add(stepId));
  };

  const toggleActive = async () => {
    if (!sequence) return;
    setSaving('toggle');
    try {
      const next = !sequence.is_active;
      await emailSequenceService.update(sequence.id, { is_active: next });
      setSequence({ ...sequence, is_active: next });
    } finally {
      setSaving(null);
    }
  };

  // ── Memoized display bits ──
  const blocks = useMemo<SequenceBlockData[]>(
    () =>
      steps.map((s) => ({
        id: s.id,
        step_order: s.step_order,
        subject: s.subject,
        body_html: s.body_html,
      })),
    [steps],
  );

  // ── Render ──
  if (loading) return <FullPageSpinner />;

  if (error || !sequence) {
    return (
      <div className="max-w-2xl mx-auto mt-10">
        <EmptyState
          icon={AlertCircle}
          title="Sequence not found"
          description={error ?? 'This sequence may have been deleted.'}
          action={
            <Link
              to="/emails/sequences"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[var(--vscode-border-primary)] text-[13px] vscode-text-secondary hover:vscode-text-primary"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sequences
            </Link>
          }
        />
      </div>
    );
  }

  const hasDirty = dirtyIds.size > 0;
  const readOnly = sequence.is_active;

  return (
    <div>
      <PageHeader
        title={sequence.name}
        subtitle={sequence.description || undefined}
        breadcrumb={[
          { label: 'Emails', to: '/emails' },
          { label: 'Sequences', to: '/emails/sequences' },
          { label: sequence.name },
        ]}
        action={
          <div className="flex items-center gap-2">
            {hasDirty && (
              <button
                type="button"
                onClick={saveAllDirty}
                disabled={saving === 'bulk'}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 text-white text-[13px] font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving === 'bulk' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                Save changes ({dirtyIds.size})
              </button>
            )}
            <button
              type="button"
              onClick={toggleActive}
              disabled={saving === 'toggle'}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[var(--vscode-border-primary)] text-[13px] vscode-text-primary hover:bg-[var(--vscode-bg-hover)] disabled:opacity-50"
            >
              {sequence.is_active ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
                  Disable sequence
                </>
              ) : (
                <>
                  <span className="h-2 w-2 rounded-full bg-zinc-400" aria-hidden />
                  Enable sequence
                </>
              )}
            </button>
          </div>
        }
      />

      {/* Inner tabs */}
      <div
        className="flex items-center border-b border-[var(--vscode-border-primary)] mb-4 px-0 sticky top-0 z-10 bg-[var(--vscode-bg-primary)]"
        role="tablist"
        aria-label="Sequence sections"
        style={{ height: 44 }}
      >
        {([
          { key: 'editor' as const, label: 'Editor' },
          { key: 'recipients' as const, label: 'Recipients' },
        ]).map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={[
                'relative flex h-full items-center px-4 transition-colors',
                active
                  ? 'text-[13px] font-medium vscode-text-primary'
                  : 'text-[11px] font-normal vscode-text-tertiary uppercase tracking-wide hover:vscode-text-secondary',
              ].join(' ')}
            >
              {t.label}
              {active && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-emerald-500" />
              )}
            </button>
          );
        })}
      </div>

      {tab === 'editor' && (
        <EditorTab
          blocks={blocks}
          waits={steps.reduce<Record<string, number>>((acc, s) => {
            acc[s.id] = local.waits[s.id] ?? s.delay_days;
            return acc;
          }, {})}
          onPatchBlock={patchStep}
          onDeleteBlock={deleteBlock}
          onChangeWait={updateWait}
          onAddBlock={addBlock}
          readOnly={readOnly}
          isAddingBlock={saving === 'step'}
          settings={local.settings}
          onChangeSettings={updateSettings}
          senderEmail={user?.email ?? null}
        />
      )}

      {tab === 'recipients' && <RecipientsTab sequenceId={sequence.id} />}
    </div>
  );
}

// ── Editor tab layout ─────────────────────────────────────────────────────────

function EditorTab({
  blocks,
  waits,
  onPatchBlock,
  onDeleteBlock,
  onChangeWait,
  onAddBlock,
  readOnly,
  isAddingBlock,
  settings,
  onChangeSettings,
  senderEmail,
}: {
  blocks: SequenceBlockData[];
  waits: Record<string, number>;
  onPatchBlock: (stepId: string, patch: Partial<Pick<EmailSequenceStep, 'subject' | 'body_html'>>) => void;
  onDeleteBlock: (stepId: string) => void;
  onChangeWait: (stepId: string, days: number) => void;
  onAddBlock: () => void;
  readOnly: boolean;
  isAddingBlock: boolean;
  settings: SequenceSettings;
  onChangeSettings: (patch: Partial<SequenceSettings>) => void;
  senderEmail: string | null;
}) {
  return (
    <div className="flex gap-6 items-start">
      {/* Canvas (vertical chain) */}
      <div className="flex-1 min-w-0 max-w-[760px] mx-auto">
        {readOnly && (
          <div className="mb-4 flex items-start gap-2 rounded-[10px] border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] vscode-text-primary">
            <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
            <span>
              Sequence is <b>active</b>. Disable it before editing blocks to avoid disrupting in-flight sends.
            </span>
          </div>
        )}

        {blocks.length === 0 ? (
          <div className="rounded-[12px] border border-dashed border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)] py-16 text-center">
            <p className="text-[13px] vscode-text-secondary">No blocks yet.</p>
            <button
              type="button"
              onClick={onAddBlock}
              disabled={isAddingBlock}
              className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 text-white text-[13px] font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {isAddingBlock ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add first block
            </button>
          </div>
        ) : (
          <div>
            {blocks.map((b, idx) => (
              <div key={b.id}>
                <SequenceBlockCard
                  block={b}
                  onChange={(patch) => onPatchBlock(b.id, patch)}
                  onDelete={() => onDeleteBlock(b.id)}
                  readOnly={readOnly}
                />
                {idx < blocks.length - 1 && (
                  <SequenceWaitConnector
                    days={waits[blocks[idx + 1]!.id] ?? 3}
                    onChange={(days) => onChangeWait(blocks[idx + 1]!.id, days)}
                    readOnly={readOnly}
                  />
                )}
              </div>
            ))}

            {/* Add-block button */}
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={onAddBlock}
                disabled={isAddingBlock || readOnly}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-dashed border-[var(--vscode-border-primary)] text-[12px] vscode-text-secondary hover:vscode-text-primary hover:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAddingBlock ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add block
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right rail (sticky) */}
      <div className="hidden lg:block sticky top-14 self-start">
        <SequenceSettingsRail
          settings={settings}
          onChange={onChangeSettings}
          senderEmail={senderEmail}
        />
      </div>
    </div>
  );
}

// ── Recipients tab (C-21: wired to sequenceRecipientsService) ────────────────

const STATUS_CHIP: Record<SequenceRecipientStatus, { label: string; cls: string }> = {
  active:       { label: 'Active',       cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  paused:       { label: 'Paused',       cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  completed:    { label: 'Completed',    cls: 'bg-zinc-500/15 vscode-text-secondary' },
  unsubscribed: { label: 'Unsubscribed', cls: 'bg-red-500/15 text-red-500 dark:text-red-400' },
};

function StatusChip({ status }: { status: SequenceRecipientStatus }) {
  const { label, cls } = STATUS_CHIP[status];
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded-full text-[11px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

function formatEnrolled(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function RecipientsTab({ sequenceId }: { sequenceId: string }) {
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const recipientsQuery = useQuery<SequenceRecipient[]>({
    queryKey: queryKeys.sequences.recipients(sequenceId),
    queryFn: () => sequenceRecipientsService.list(sequenceId),
    staleTime: 15_000,
  });

  const handleUnenroll = useCallback(
    async (recipient: SequenceRecipient) => {
      if (!window.confirm(`Unenrol ${recipient.candidateName} from this sequence?`)) return;
      try {
        await sequenceRecipientsService.unenroll(sequenceId, recipient.id);
        await queryClient.invalidateQueries({
          queryKey: queryKeys.sequences.recipients(sequenceId),
        });
        showToast({
          variant: 'success',
          title: 'Unenrolled',
          body: `${recipient.candidateName} removed from the sequence.`,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        showToast({ variant: 'error', title: 'Could not unenrol', body: msg });
      }
    },
    [sequenceId, queryClient, showToast],
  );

  if (recipientsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16 vscode-text-secondary" aria-busy="true">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
        <span className="text-[13px]">Loading recipients…</span>
      </div>
    );
  }

  if (recipientsQuery.isError) {
    return (
      <div className="rounded-[10px] border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-center gap-3">
        <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden />
        <p className="flex-1 text-[13px] vscode-text-primary">
          Could not load recipients.
        </p>
        <button
          type="button"
          onClick={() => { void recipientsQuery.refetch(); }}
          className="inline-flex items-center gap-1.5 rounded-[8px] border px-3 py-1 text-[12px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
        >
          <RotateCw className="h-3.5 w-3.5" aria-hidden />
          Retry
        </button>
      </div>
    );
  }

  const rows = recipientsQuery.data ?? [];

  if (rows.length === 0) {
    return (
      <div className="rounded-[12px] border border-dashed border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)] py-16 text-center">
        <p className="text-[13px] vscode-text-secondary">
          No one enrolled yet — add from Smart Match or a candidate list.
        </p>
        <p className="mt-1 text-[11px] vscode-text-tertiary">
          Use Smart Match’s “Create sequence” chip or pick candidates from a list view to enrol them here.
        </p>
      </div>
    );
  }

  return (
    <div className="ggj-data-table rounded-[12px] border border-[var(--vscode-border-primary)] overflow-hidden">
      <table className="w-full">
        <thead>
          <tr>
            <th style={{ width: 40 }} />
            <th>Candidate</th>
            <th>Enrolled</th>
            <th>Status</th>
            <th style={{ width: 40 }} aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <RecipientRow key={r.id} recipient={r} onUnenroll={handleUnenroll} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecipientRow({
  recipient,
  onUnenroll,
}: {
  recipient: SequenceRecipient;
  onUnenroll: (r: SequenceRecipient) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = () => setMenuOpen(false);
    window.addEventListener('click', onDoc);
    return () => window.removeEventListener('click', onDoc);
  }, [menuOpen]);

  return (
    <tr>
      <td>
        <TableAvatar name={recipient.candidateName} />
      </td>
      <td>
        <div className="text-[13px] font-medium vscode-text-primary">
          {recipient.candidateName}
        </div>
        {recipient.candidateEmail ? (
          <div className="text-[11px] vscode-text-tertiary">{recipient.candidateEmail}</div>
        ) : null}
      </td>
      <td>
        <span className="text-[12px] vscode-text-secondary">
          {formatEnrolled(recipient.enrolledAt)}
        </span>
      </td>
      <td>
        <StatusChip status={recipient.status} />
      </td>
      <td className="relative">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          aria-label={`Actions for ${recipient.candidateName}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </button>
        {menuOpen ? (
          <div
            className="absolute right-2 top-9 z-20 w-[180px] rounded-[8px] border p-1 shadow-lg"
            style={{
              background: 'var(--vscode-bg-elevated)',
              borderColor: 'var(--vscode-border-primary)',
            }}
            role="menu"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onUnenroll(recipient);
              }}
              disabled={recipient.status === 'unsubscribed'}
              className="flex w-full items-center gap-2 rounded-[6px] px-2 py-1.5 text-left text-[12px] vscode-text-primary transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <UserMinus className="h-3.5 w-3.5" aria-hidden />
              Unenrol
            </button>
          </div>
        ) : null}
      </td>
    </tr>
  );
}
